import { useState } from 'react'
import { ScrollText } from 'lucide-react'
import {
  EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useSettlementConfigs, useSettlementMutations } from '../api'
import type { SettlementConfigView } from '../types'
import { Select } from '@/components/ui/select'

/**
 * End-of-service terms per legal entity.
 *
 * The defaults are Decree-Law 33/2021 article 51 and the citation is shown beside them, so
 * nobody has to take the software's word for where the figures came from. The two switches
 * that reduce what somebody is paid — the resignation reduction and the legacy limited-
 * contract ladder — start off and are never inferred from contract data.
 */
export function SettlementTermsPage() {
  useBreadcrumb()
  const configs = useSettlementConfigs()
  const { saveConfig } = useSettlementMutations()

  const [draft, setDraft] = useState<SettlementConfigView | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="End-of-service terms"
        subtitle="What gratuity is worked out from, per legal entity."
      />

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <ScrollText className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>
          Defaults are <strong>UAE Federal Decree-Law 33/2021, article 51</strong>: twenty-one
          days' basic wage for each of the first five years of service, thirty days for each
          year after, capped at two years' wage, on the basic wage with allowances excluded,
          after a year's minimum service. Change them only where your contracts are more
          generous — never less.
        </span>
      </p>

      <Card>
        {configs.isPending && <Skeleton className="m-4 h-24" />}
        {configs.isError && (
          <ErrorState error={configs.error} onRetry={() => void configs.refetch()} />
        )}
        {configs.isSuccess && configs.data.length === 0 && (
          <EmptyState
            title="No entity has its own terms yet"
            description="Each entity gets the statutory defaults, written down as a row, the first time a settlement is calculated for it."
          />
        )}
        {configs.isSuccess && configs.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Entity</th>
                  <th className="p-3 text-end font-medium">First years</th>
                  <th className="p-3 text-end font-medium">Later years</th>
                  <th className="p-3 text-end font-medium">Cap</th>
                  <th className="p-3 text-start font-medium">Resignation</th>
                  <th className="p-3 text-start font-medium">Terms</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {configs.data.map((config) => (
                  <tr key={config.id} className="border-b border-slate-100 align-top">
                    <td className="p-3 font-mono text-xs">{config.legalEntityId}</td>
                    <td className="p-3 text-end">
                      {config.daysPerYearFirstBand} days
                      <div className="text-xs text-slate-600">
                        first {config.bandBoundaryYears}
                      </div>
                    </td>
                    <td className="p-3 text-end">{config.daysPerYearLaterBand} days</td>
                    <td className="p-3 text-end">
                      {config.capInYearsOfWage == null
                        ? 'Uncapped' : `${config.capInYearsOfWage} years' wage`}
                    </td>
                    <td className="p-3">
                      {config.reduceOnResignation ? (
                        <span className="text-amber-700">Reduced</span>
                      ) : (
                        <span className="text-slate-600">Not reduced</span>
                      )}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={config.statutoryDefault ? 'STATUTORY' : 'CUSTOM'}
                        tone={config.statutoryDefault ? 'success' : 'info'}
                        label={config.statutoryDefault ? 'Statutory default' : 'Changed'}
                      />
                      <p className="mt-1 max-w-xs text-xs text-slate-600">
                        {config.statutoryReference}
                      </p>
                    </td>
                    <td className="p-3 text-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => { setError(null); setDraft(config) }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title="End-of-service terms"
        description="A settlement already calculated keeps the terms it was calculated under; these apply to the next one."
        submitLabel="Save terms"
        loading={saveConfig.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveConfig.mutateAsync({ ...draft })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'Those terms could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="terms-first">Days a year, first band</Label>
                <Input
                  id="terms-first" type="number" value={draft.daysPerYearFirstBand}
                  onChange={(event) => setDraft({
                    ...draft, daysPerYearFirstBand: Number(event.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="terms-later">Days a year, after</Label>
                <Input
                  id="terms-later" type="number" value={draft.daysPerYearLaterBand}
                  onChange={(event) => setDraft({
                    ...draft, daysPerYearLaterBand: Number(event.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="terms-boundary">Band changes after</Label>
                <Input
                  id="terms-boundary" type="number" value={draft.bandBoundaryYears}
                  onChange={(event) => setDraft({
                    ...draft, bandBoundaryYears: Number(event.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="terms-cap">Cap, in years of wage</Label>
                <Input
                  id="terms-cap" type="number" value={draft.capInYearsOfWage ?? ''}
                  onChange={(event) => setDraft({
                    ...draft,
                    capInYearsOfWage: event.target.value === '' ? null : Number(event.target.value),
                  })}
                />
                <p className="mt-1 text-xs text-slate-600">Blank means uncapped.</p>
              </div>
              <div>
                <Label htmlFor="terms-minimum">Minimum service, months</Label>
                <Input
                  id="terms-minimum" type="number" value={draft.minimumServiceMonths}
                  onChange={(event) => setDraft({
                    ...draft, minimumServiceMonths: Number(event.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="terms-divisor">Days in a month</Label>
                <Input
                  id="terms-divisor" type="number" value={draft.daysInMonthDivisor}
                  onChange={(event) => setDraft({
                    ...draft, daysInMonthDivisor: Number(event.target.value) })}
                />
                <p className="mt-1 text-xs text-slate-600">
                  What the monthly wage is divided by to reach a daily one. Thirty is
                  conventional, not statutory.
                </p>
              </div>
            </div>

            <fieldset className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
              <legend className="px-1 text-sm font-medium text-amber-900">
                These reduce what somebody is paid
              </legend>
              <label className="flex items-start gap-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={draft.reduceOnResignation}
                  onChange={(event) => setDraft({
                    ...draft, reduceOnResignation: event.target.checked })}
                />
                <span>
                  Reduce gratuity when somebody resigns — a third under three years, two thirds
                  under five. This belonged to the 1980 law and does not apply to unlimited
                  contracts under the current one. Off unless your contracts genuinely carry it.
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={draft.legacyLimitedContractLadder}
                  onChange={(event) => setDraft({
                    ...draft, legacyLimitedContractLadder: event.target.checked })}
                />
                <span>
                  Use the legacy limited-contract ladder, for contracts predating the 2022
                  transition only.
                </span>
              </label>
            </fieldset>

            {draft.recoverUnreturnedAssets && (
              <div>
                <Label htmlFor="settlement-asset-valuation">
                  What an unreturned asset is charged at
                </Label>
                <Select
                  id="settlement-asset-valuation"
                  className="w-full"
                  value={draft.assetRecoveryValuation ?? ''}
                  onChange={(event) => setDraft({
                    ...draft,
                    assetRecoveryValuation: event.target.value || null,
                  })}
                >
                  <option value="">Nothing is charged — only listed</option>
                  <option value="BOOK_VALUE">Book value on the last working day</option>
                  <option value="RECOVERY_VALUE">The recovery value set on the asset</option>
                </Select>
                <p className="mt-1 text-xs text-slate-600">
                  No default. The purchase price of a four-year-old laptop and nothing at all
                  are both defensible, so with nothing chosen the settlement names the assets
                  and charges nothing rather than inventing a figure.
                </p>
              </div>
            )}

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">What the settlement includes</legend>
              {([
                ['includeUnusedLeave', 'Encash unused leave'],
                ['includeAirTicket', 'Settle the repatriation air ticket'],
                ['includeNoticePay', 'Pay unserved notice in lieu'],
                ['recoverOutstandingLoans', 'Recover outstanding loan balances'],
                ['recoverUnreturnedAssets', 'Recover unreturned assets'],
              ] as const).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft[key]}
                    onChange={(event) => setDraft({ ...draft, [key]: event.target.checked })}
                  />
                  {label}
                </label>
              ))}
            </fieldset>

            <div>
              <Label htmlFor="terms-reference">Where these terms come from</Label>
              <Input
                id="terms-reference" value={draft.statutoryReference ?? ''}
                onChange={(event) => setDraft({ ...draft, statutoryReference: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Printed on every settlement statement calculated under them.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
